import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Code2,
  TrendingUp,
  Award,
  Heart,
  Users,
  MapPin,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Quote,
} from "lucide-react";

const pillars = [
  {
    icon: Code2,
    title: "Be a Creator",
    description:
      "We build scalable, AI-enabled healthcare products — not projects. Your engineering, design, and strategic contributions become core components of a system used by thousands. This isn't a service company; it's an innovation mission.",
    accent: "text-primary",
    bg: "bg-primary/10",
    bar: "from-primary to-primary/40",
  },
  {
    icon: TrendingUp,
    title: "Employee Stock Benefits",
    description:
      "We believe those who build the future should share in its rewards. Our Employee Stock Benefits Scheme ensures that as Wellnext grows, your wealth, your freedom, and your future grow with it.",
    accent: "text-secondary",
    bg: "bg-secondary/10",
    bar: "from-secondary to-secondary/40",
  },
  {
    icon: Award,
    title: "Led by Industry Veterans",
    description:
      "Founded by a seasoned technologist with 30 years of experience, including 20+ years of entrepreneurial leadership and multiple successful international projects. Learn directly from people who've built real, impactful solutions.",
    accent: "text-accent",
    bg: "bg-accent/10",
    bar: "from-accent to-accent/40",
  },
  {
    icon: Heart,
    title: "Work That Matters",
    description:
      "Our products are not just commercial solutions — they improve lives. We build tools that help doctors, clinics, and patients access healthcare more easily and intelligently. Your skills directly uplift communities.",
    accent: "text-primary",
    bg: "bg-primary/10",
    bar: "from-primary to-primary/40",
  },
  {
    icon: Users,
    title: "Share in the Success",
    description:
      "Wellnext is committed to complete transparency. We share year-on-year profits with all employees, and a defined portion is reinvested into the team.",
    accent: "text-secondary",
    bg: "bg-secondary/10",
    bar: "from-secondary to-secondary/40",
    benefits: ["Employee welfare", "Career development", "Upskilling & certifications", "Team growth & benefits"],
  },
  {
    icon: MapPin,
    title: "India's Health-Tech Revolution",
    description:
      "Millions outside urban India still lack awareness, access, and timely health information. Technology can change this — and you can be the one building it. World-class, made-in-India solutions at scale.",
    accent: "text-accent",
    bg: "bg-accent/10",
    bar: "from-accent to-accent/40",
  },
];

const JoinUs = () => {
  return (
    <Layout>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-primary/8 via-background to-secondary/8">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="container mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-8">
            <Sparkles className="h-3.5 w-3.5" />
            Careers at Wellnext
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-[1.1]">
            <span className="text-gradient-primary">Why join us?</span>
          </h1>

          <p className="text-2xl md:text-3xl font-semibold text-foreground/80 mb-6">
            Here you don't just work…
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {["You Build.", "You Own.", "You Grow."].map((item) => (
              <span
                key={item}
                className="px-6 py-2.5 rounded-full bg-white/80 backdrop-blur border border-border/60 shadow-sm text-lg font-bold text-foreground"
              >
                {item}
              </span>
            ))}
          </div>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            At Wellnext Technologies, we are looking for builders, innovators, and future owners
            of a game-changing health-tech ecosystem.
          </p>
        </div>
      </section>

      {/* ─── Six Pillars ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              What sets us apart
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient-primary">Six reasons to build with us</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map(({ icon: Icon, title, description, accent, bg, bar, benefits }) => (
              <div
                key={title}
                className="group rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500 overflow-hidden flex flex-col"
              >
                <div className={`h-[3px] w-full bg-gradient-to-r ${bar}`} />
                <div className="p-8 flex-1 flex flex-col">
                  <div className={`h-12 w-12 rounded-2xl ${bg} flex items-center justify-center mb-5`}>
                    <Icon className={`h-6 w-6 ${accent}`} />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">{description}</p>
                  {benefits && (
                    <ul className="mt-auto space-y-2">
                      {benefits.map((b) => (
                        <li key={b} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className={`h-3.5 w-3.5 ${accent} shrink-0`} />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Made in India Banner ─────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/feature_made_in_india.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/92 via-primary/78 to-secondary/75" />
        </div>
        <div className="relative container mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/30 text-white/90 text-sm font-semibold mb-6">
            <MapPin className="h-3.5 w-3.5" />
            🇮🇳 Made in India
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 max-w-3xl mx-auto leading-tight">
            Build it. Own it. Enjoy the rewards.
          </h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Join Wellnext Technologies and be a core part of shaping the future of healthcare in
            India and beyond. At the end of the day, our promise is simple — you will build, you
            will grow, and you will enjoy the rewards.
          </p>
        </div>
      </section>

      {/* ─── Closing Quote ────────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex h-12 w-12 rounded-2xl bg-primary/10 items-center justify-center mb-8">
              <Quote className="h-6 w-6 text-primary" />
            </div>
            <blockquote className="text-2xl md:text-3xl font-semibold text-foreground leading-relaxed mb-6">
              "Healthcare in India is still one of the most unorganized yet fastest-growing sectors.
              If we can build world-class, made-in-India solutions at scale, the sky is not the
              limit — it's just the beginning."
            </blockquote>
            <p className="text-muted-foreground mb-12">
              — Sumantra Mukherjee, Founder & CEO, Wellnext Technologies
            </p>

            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/human-resource">
                <Button size="lg" className="gap-2 rounded-xl shadow-[0_4px_14px_rgba(0,0,0,0.12)]">
                  View Current Openings
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default JoinUs;
